import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { cn } from '@/lib/core/utils/cn';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { ScreenHead } from '@/components/shared/screen/head';
import {
  useExploreLocations,
  useExploreCategories,
  useExploreVenues,
} from '@/services/platform/data/explore';
import type { VenueGroupVenueDto } from '@/services/platform/api/model';
import { resolveMediaUrl } from '../ui/resolveMediaUrl';
import { useVenueBookmarks } from '../ui/useVenueBookmarks';
import { LOCATION_DROPDOWN_PANEL_ID, LocationDropdown } from './location-dropdown';
import { VenueSheet } from './venue-sheet';

// ── Types ────────────────────────────────────────────────────────────────────

interface ExploreMapLocationState {
  locationNodeKey?: string;
  activeCategoryNodeKey?: string | null;
  activeVenueGroupNodeKey?: string | null;
  isSheetExpanded?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const LAST_LOCATION_KEY = 'explore:lastLocationNodeKey';

function readLastLocationNodeKey(): string | undefined {
  try {
    return sessionStorage.getItem(LAST_LOCATION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeLastLocationNodeKey(nodeKey: string): void {
  try {
    sessionStorage.setItem(LAST_LOCATION_KEY, nodeKey);
  } catch {
    // storage quota exceeded — skip caching
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface CategoryTabProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function CategoryTab({ label, isActive, onClick }: CategoryTabProps) {
  return (
    <Button
      variant="tertiary-link"
      palette="dark"
      onClick={onClick}
      className={cn(
        'h-auto min-w-0 flex-col gap-0.5 rounded-none px-0 py-1 text-[14px] tracking-[0.5px]',
        isActive && 'font-semibold'
      )}
    >
      <span>{label}</span>
      <span
        className={cn('h-px w-4 rounded-full', isActive ? 'bg-jncquoi-neutral-1000' : 'bg-transparent')}
      />
    </Button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ExploreMapScreen() {
  const { state } = useLocation() as { state: ExploreMapLocationState | null };
  const locationNodeKey = state?.locationNodeKey ?? readLastLocationNodeKey();
  const t = useClientTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const setMainClassName = useUIStore(state => state.setMainClassName);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  const [activeCategoryNodeKey, setActiveCategoryNodeKey] = useState<string | null>(
    state?.activeCategoryNodeKey ?? null
  );
  const [activeVenueGroupNodeKey, setActiveVenueGroupNodeKey] = useState<string | null>(
    state?.activeVenueGroupNodeKey ?? null
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSheetExpanded, setIsSheetExpanded] = useState(state?.isSheetExpanded ?? false);
  const { isBookmarked, toggleBookmark } = useVenueBookmarks();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapWidth, setMapWidth] = useState(0);
  // Measured from the loaded `<img>` itself rather than trusted from `mapImage.width/height` —
  // the map images are SVGs, and the CMS's Delivery API returns `width`/`height` as `null` for
  // that media type, so the metadata isn't just unreliable here, it's simply absent.
  const [mapImageNaturalSize, setMapImageNaturalSize] = useState<{ width: number; height: number } | null>(
    null
  );

  const { locations } = useExploreLocations();
  const currentLocation = locations.find(l => l.nodeKey === locationNodeKey);

  const { categories } = useExploreCategories({ locationNodeKey }, { enabled: !!locationNodeKey });
  const { venues } = useExploreVenues(
    {
      locationNodeKey: locationNodeKey ?? undefined,
      serviceCategoryNodeKey: activeCategoryNodeKey ?? undefined,
    },
    { enabled: !!locationNodeKey }
  );
  const mapUrl = resolveMediaUrl(currentLocation?.mapImage?.url);
  // Zoom factor for the map "stage" (image + pins, scaled as one rigid unit): fit to the
  // container's width only, so the map is always full-bleed edge-to-edge and never letterboxed
  // left/right. Height is whatever that scale works out to — taller than the viewport scrolls
  // rather than shrinking further to fit, so nothing is ever cropped.
  const mapScale = mapImageNaturalSize && mapWidth > 0 ? mapWidth / mapImageNaturalSize.width : null;
  const resultCount = venues.reduce((acc, g) => acc + (g.venues?.length ?? 1), 0);
  const sheetVenues = activeVenueGroupNodeKey
    ? venues.filter(g => g.venueNodeKey === activeVenueGroupNodeKey)
    : venues;
  const activeGroup = activeVenueGroupNodeKey ? (sheetVenues[0] ?? null) : null;
  const activeCategory = categories.find(c => c.nodeKey === activeCategoryNodeKey);
  const activeCategoryLabel = activeCategory?.name ?? t('App.Explore.Map.All');
  const activeCategoryTitle = activeCategory?.title ?? categories[0]?.title ?? '';

  useEffect(() => {
    setMainClassName('pb-0');
    setHideNavbar(true);
    return () => {
      setMainClassName('');
      setHideNavbar(false);
    };
  }, [setMainClassName, setHideNavbar]);

  useEffect(() => {
    if (locationNodeKey) writeLastLocationNodeKey(locationNodeKey);
  }, [locationNodeKey]);

  // Cleared whenever the image changes — a new location, or a revalidated list bringing a new
  // `mapImage` for the same one — so a stale image's dimensions aren't used against the new one
  // while it loads.
  useEffect(() => {
    setMapImageNaturalSize(null);
  }, [mapUrl]);

  // Measured rather than read off the ref during render, which yielded 0 on first paint and left
  // the sheet's max height at `calc(100svh - 0px)`. Observed because the header grows once the
  // category tabs land, and on rotation.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    setHeaderHeight(el.offsetHeight);
    const observer = new ResizeObserver(([entry]) => setHeaderHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Measured so the map "stage" can be scaled to exactly fill the container's width.
  useLayoutEffect(() => {
    const el = mapRef.current;
    if (!el) return;

    setMapWidth(el.offsetWidth);
    const observer = new ResizeObserver(([entry]) => setMapWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Keep this history entry's state in sync with the current view, so that
  // navigating to venue-detail and back (navigate(-1)) restores the same
  // category/pin/sheet selection instead of resetting to defaults.
  useEffect(() => {
    if (window.location.pathname !== routerLocation.pathname) return;
    navigate(routerLocation.pathname, {
      replace: true,
      state: { locationNodeKey, activeCategoryNodeKey, activeVenueGroupNodeKey, isSheetExpanded },
    });
  }, [activeCategoryNodeKey, activeVenueGroupNodeKey, isSheetExpanded]);

  function handleCategorySelect(nodeKey: string | null) {
    setActiveCategoryNodeKey(nodeKey);
    setActiveVenueGroupNodeKey(null);
  }

  function handlePinSelect(venueNodeKey: string | null) {
    const isDeselecting = activeVenueGroupNodeKey === venueNodeKey;
    setActiveVenueGroupNodeKey(isDeselecting ? null : venueNodeKey);
    setIsSheetExpanded(!isDeselecting);
  }

  function handleLocationSelect(loc: { nodeKey?: string; name?: string | null }) {
    setIsDropdownOpen(false);
    if (loc.nodeKey === locationNodeKey) return;
    setActiveCategoryNodeKey(null);
    setActiveVenueGroupNodeKey(null);
    setIsSheetExpanded(false);
    navigate(routerLocation.pathname, {
      replace: true,
      state: {
        locationNodeKey: loc.nodeKey,
        activeCategoryNodeKey: null,
        activeVenueGroupNodeKey: null,
        isSheetExpanded: false,
      },
    });
  }

  function handleSheetCollapse() {
    setIsSheetExpanded(false);
    setActiveVenueGroupNodeKey(null);
  }

  function handleVenueTap(venue: VenueGroupVenueDto, groupName?: string | null) {
    if (!venue.nodeKey) return;
    navigate(`/venue-detail/${venue.nodeKey}`, {
      state: { venue, venueGroupName: groupName ?? activeGroup?.name },
    });
  }

  return (
    <div className="bg-jncquoi-avenida-465c-50 relative flex min-h-svh flex-col">
      {/* ── Sticky header ───────────────────────────────────────────── */}
      <div ref={headerRef} className="sticky top-0 z-20">
        <div className="bg-jncquoi-neutral-00 pt-safe-area flex flex-col">
          <ScreenHead
            title={t('App.Explore.Title')}
            onBack={() => navigate(-1)}
            ariaLabel={t('App.Labels.GoBack')}
            className="pt-0"
          />

          {/*
            Location selector. The whole row is the control, not just the chevron — the tagline
            and the hub name read as the thing you tap. The button sits inside the heading (the
            ARIA disclosure pattern) so `h2` survives; its accessible name comes from the text it
            wraps, which beats the "Explore" label the icon-only button used to carry.
          */}
          {/* `relative` so the dropdown panel below can anchor right under the heading,
              covering the tabs — matches Figma, where the tabs are replaced by the list —
              without changing the header's own height (which would move the map). */}
          <div className="relative">
            <Heading as="h2" className="px-4 pt-6.25 pb-4">
              <button
                type="button"
                aria-expanded={isDropdownOpen}
                aria-controls={LOCATION_DROPDOWN_PANEL_ID}
                className="flex w-full cursor-pointer items-start justify-between text-left"
                onClick={() => setIsDropdownOpen(v => !v)}
              >
                <span className="flex flex-col items-start gap-0.5 pl-2">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[24px] leading-none font-semibold">
                    {t('App.Explore.Map.LocationTagline')}
                  </span>
                  <span className="font-didot text-jncquoi-neutral-1000 text-[36px] leading-8.5 tracking-[-1.8px] uppercase italic">
                    {currentLocation?.name ?? '—'}
                  </span>
                </span>

                <span className="flex size-11 shrink-0 items-center justify-center" aria-hidden="true">
                  <Icon
                    name={isDropdownOpen ? 'nav-arrow-up' : 'nav-arrow-down'}
                    width={24}
                    height={24}
                    className="text-jncquoi-neutral-1000"
                  />
                </span>
              </button>
            </Heading>

            {isDropdownOpen && (
              <LocationDropdown
                locations={locations}
                activeNodeKey={locationNodeKey}
                onSelect={handleLocationSelect}
                className="absolute inset-x-0 top-full z-10 shadow-lg"
              />
            )}
          </div>

          {/* Stays mounted (so the header's height, and the map below it, never moves) but
              `inert` and visually covered by the dropdown panel above while it is open. */}
          <div className="scrollbar-hide overflow-x-auto px-4 pb-1" inert={isDropdownOpen}>
            <div className="flex gap-4">
              <CategoryTab
                label={t('App.Explore.Map.All')}
                isActive={activeCategoryNodeKey === null}
                onClick={() => handleCategorySelect(null)}
              />
              {categories.map(cat => (
                <CategoryTab
                  key={cat.nodeKey}
                  label={cat.name ?? ''}
                  isActive={activeCategoryNodeKey === cat.nodeKey}
                  onClick={() => handleCategorySelect(cat.nodeKey ?? null)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Map ─────────────────────────────────────────────────────── */}
      {/* `inert` while the dropdown floats over it — otherwise its pins stay reachable by
          keyboard/screen reader underneath the opaque panel. Top-aligned and always full width
          (`mapScale` fits by width only) rather than centered/letterboxed — a map taller than
          the viewport scrolls instead of shrinking further to fit. */}
      <div
        ref={mapRef}
        className="bg-jncquoi-avenida-465c-50 relative flex-1 overflow-x-hidden overflow-y-auto"
        inert={isDropdownOpen}
      >
        {mapUrl ? (
          mapImageNaturalSize && mapScale ? (
            // Sized to the image's own natural pixels, so a pin's `mapPinX`/`mapPinY` — a
            // percentage of that same image — can be placed directly, with no per-pin offset
            // math, and always lands on the same spot in the artwork. The whole stage (image +
            // pins together) is then zoomed as one rigid unit, from its top-left corner, to fill
            // the container's width.
            <div
              className="absolute top-0 left-0"
              style={{
                width: mapImageNaturalSize.width,
                height: mapImageNaturalSize.height,
                transform: `scale(${mapScale})`,
                transformOrigin: 'top left',
              }}
            >
              <img src={mapUrl} alt={currentLocation?.name ?? ''} className="block h-full w-full" />

              {venues.map(group =>
                group.mapPinX != null && group.mapPinY != null ? (
                  <button
                    key={group.venueNodeKey}
                    type="button"
                    aria-label={group.name ?? ''}
                    className="bg-jncquoi-neutral-300 border-jncquoi-neutral-00 absolute flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2"
                    // Counter-scaled so a pin keeps its on-screen size whatever the artwork's
                    // natural width — an SVG exported with only a `viewBox` reports ~113px, which
                    // zoomed the stage (and the pins with it) by more than 3x.
                    style={{ left: `${group.mapPinX}%`, top: `${group.mapPinY}%`, scale: 1 / mapScale }}
                    onClick={() => handlePinSelect(group.venueNodeKey ?? null)}
                  >
                    <span className="bg-jncquoi-neutral-00 size-4 rounded-full" />
                  </button>
                ) : null
              )}
            </div>
          ) : (
            // Still measuring the image's natural size and/or the container — render the image
            // alone (needed to trigger `onLoad`), full width and top-aligned like the final
            // state, rather than place pins with a guess that would just be corrected a moment
            // later.
            <img
              // A ref callback runs synchronously once this element is committed — the earliest
              // point `.complete` can be checked. Needed because a revisited location's SVG is
              // already in the browser cache, so the image can finish loading before `onLoad`'s
              // listener is attached, and that event never fires.
              ref={img => {
                if (mapImageNaturalSize) return;
                if (img?.complete && img.naturalWidth && img.naturalHeight) {
                  setMapImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                }
              }}
              src={mapUrl}
              alt={currentLocation?.name ?? ''}
              className="block h-auto w-full"
              onLoad={e =>
                setMapImageNaturalSize({
                  width: e.currentTarget.naturalWidth,
                  height: e.currentTarget.naturalHeight,
                })
              }
            />
          )
        ) : (
          <div className="bg-jncquoi-neutral-100 h-full w-full" />
        )}
      </div>

      {/* ── Bottom sheet (renders its own peek bar, which is also the swipe-to-open area) ─── */}
      <VenueSheet
        isExpanded={isSheetExpanded}
        activeGroup={activeGroup}
        sheetVenues={sheetVenues}
        currentLocation={currentLocation}
        activeCategoryLabel={activeCategoryLabel}
        activeCategoryTitle={activeCategoryTitle}
        resultCount={resultCount}
        headerHeight={headerHeight}
        isBookmarked={isBookmarked}
        onExpand={() => setIsSheetExpanded(true)}
        onCollapse={handleSheetCollapse}
        onBookmark={toggleBookmark}
        onVenueTap={handleVenueTap}
      />
    </div>
  );
}
